import React, { useState, useEffect } from 'react';
import { usageBarColor } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, Pencil, Trash2, LinkIcon, Upload, Image, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import IconPicker, { RightIcon } from '@/components/IconPicker';
import { useAuth } from '@/contexts/AuthContext';

export default function RightsPage() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const [rights, setRights] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(() => localStorage.getItem('rights_show_inactive') === '1');

  // Create right dialog
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', unit_type: 'ticket', notes: '', exclusive: false, match_required: true });

  // Edit right dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [deactivateImpact, setDeactivateImpact] = useState(null);

  // Assign right dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ sponsor_id: '', right_id: '', allocation_type: 'limited', allocated_amount: '', per_match_limit: '', per_round_limit: '', notes: '' });

  // Edit assignment dialog
  const [editAssignOpen, setEditAssignOpen] = useState(false);
  const [editAssignForm, setEditAssignForm] = useState(null);
  const [confirmDeleteAssign, setConfirmDeleteAssign] = useState(false);

  // Delete right
  const [deleteRightConfirm, setDeleteRightConfirm] = useState(null);

  const [saving, setSaving] = useState(false);

  // Icon picker
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const load = async () => {
    try {
      const [r, a, s] = await Promise.all([
        api.get('/api/rights'),
        api.get('/api/assignments'),
        api.get('/api/sponsors'),
      ]);
      setRights(r.data);
      setAssignments(a.data);
      setSponsors(s.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const unitTypes = ['ticket', 'pass', 'slot', 'activation', 'branding'];

  // Filter rights
  const filteredRights = rights.filter(r => {
    const matchesSearch = r.name.toLowerCase().includes(search.toLowerCase());
    if (!showInactive && r.status !== 'active') return false;
    return matchesSearch;
  });

  // Filter assignments - hide inactive sponsors/rights
  const activeAssignments = showInactive
    ? assignments
    : assignments.filter(a => a.sponsor_status === 'active' && a.right_status === 'active');

  // Active sponsors for dropdowns
  const activeSponsors = sponsors.filter(s => s.status === 'active');
  // Active rights for dropdowns
  const activeRights = rights.filter(r => r.status === 'active');

  // How many rows the toggle is currently hiding
  const hiddenRightsCount = rights.filter(r => r.status !== 'active').length;
  const hiddenAssignCount = assignments.filter(a => a.sponsor_status !== 'active' || a.right_status !== 'active').length;
  const hiddenCount = hiddenRightsCount + hiddenAssignCount;

  // --- Right CRUD ---
  const handleCreateRight = async () => {
    if (!form.name) { toast.error(t('fill_required')); return; }
    setSaving(true);
    try {
      await api.post('/api/rights', form);
      toast.success(t('right_created'));
      setOpenCreate(false);
      setForm({ name: '', description: '', unit_type: 'ticket', notes: '', exclusive: false, match_required: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEditRight = (r) => {
    setEditForm({ ...r, _original_status: r.status });
    setDeactivateImpact(null);
    setEditOpen(true);
  };

  const handleDeleteRight = async (r) => {
    try {
      await api.delete(`/api/rights/${r.id}`);
      toast.success(`"${r.name}" deleted`);
      setDeleteRightConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
      setDeleteRightConfirm(null);
    }
  };

  const handleSaveRight = async () => {
    if (!editForm) return;
    // Deactivating has knock-on effects, so show what it will touch first
    if (editForm.status !== 'active' && editForm._original_status === 'active' && !deactivateImpact) {
      try {
        const { data } = await api.get(`/api/rights/${editForm.id}/impact`);
        setDeactivateImpact(data);
      } catch { toast.error('Could not check what this right is used by'); }
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/rights/${editForm.id}`, {
        name: editForm.name,
        description: editForm.description,
        unit_type: editForm.unit_type,
        status: editForm.status,
        notes: editForm.notes,
        icon_url: editForm.icon_url || '',
        exclusive: editForm.exclusive || false,
        match_required: editForm.match_required !== false,
      });
      toast.success(t('right_updated') || 'Right updated');
      setEditOpen(false);
      setEditForm(null);
      setDeactivateImpact(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleIconUpload = async (rightId, file) => {
    setUploadingIcon(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/api/rights/${rightId}/icon`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (editForm && editForm.id === rightId) {
        setEditForm(p => ({ ...p, icon_url: res.data.icon_url }));
      }
      toast.success('Icon uploaded');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingIcon(false); }
  };

  const handleRemoveIcon = async (rightId) => {
    try {
      await api.delete(`/api/rights/${rightId}/icon`);
      if (editForm && editForm.id === rightId) {
        setEditForm(p => ({ ...p, icon_url: '' }));
      }
      toast.success('Icon removed');
      load();
    } catch { toast.error('Failed to remove icon'); }
  };

  // --- Assignment CRUD ---
  const handleAssign = async () => {
    if (!assignForm.sponsor_id || !assignForm.right_id) { toast.error(t('fill_required')); return; }
    if (assignForm.allocation_type === 'limited' && (!assignForm.allocated_amount || Number(assignForm.allocated_amount) <= 0)) {
      toast.error('Allocated amount is required for limited type'); return;
    }
    setSaving(true);
    try {
      await api.post('/api/assignments', {
        sponsor_id: assignForm.sponsor_id,
        right_id: assignForm.right_id,
        allocation_type: assignForm.allocation_type,
        allocated_amount: assignForm.allocation_type === 'limited' ? Number(assignForm.allocated_amount) : null,
        per_match_limit: assignForm.per_match_limit ? Number(assignForm.per_match_limit) : null,
        per_round_limit: assignForm.per_round_limit ? Number(assignForm.per_round_limit) : null,
        notes: assignForm.notes,
      });
      toast.success(t('right_assigned') || 'Right assigned successfully');
      setAssignOpen(false);
      setAssignForm({ sponsor_id: '', right_id: '', allocation_type: 'limited', allocated_amount: '', per_match_limit: '', per_round_limit: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
    finally { setSaving(false); }
  };

  const handleEditAssignment = (a) => {
    setEditAssignForm({ ...a });
    setEditAssignOpen(true);
  };

  const handleSaveAssignment = async () => {
    if (!editAssignForm) return;
    setSaving(true);
    try {
      await api.put(`/api/assignments/${editAssignForm.id}`, {
        allocation_type: editAssignForm.allocation_type,
        allocated_amount: editAssignForm.allocation_type === 'limited' ? Number(editAssignForm.allocated_amount) : null,
        per_match_limit: editAssignForm.per_match_limit ? Number(editAssignForm.per_match_limit) : null,
        per_round_limit: editAssignForm.per_round_limit ? Number(editAssignForm.per_round_limit) : null,
        notes: editAssignForm.notes,
        match_required: editAssignForm.match_required,
      });
      toast.success(t('assignment_updated') || 'Assignment updated');
      setEditAssignOpen(false);
      setEditAssignForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDeleteAssignment = async () => {
    if (!editAssignForm) return;
    setSaving(true);
    try {
      await api.delete(`/api/assignments/${editAssignForm.id}`);
      toast.success(t('assignment_deleted') || 'Assignment deleted');
      setEditAssignOpen(false);
      setEditAssignForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    finally { setSaving(false); setConfirmDeleteAssign(false); }
  };

  // Group assignments by sponsor
  const groupedBySponsor = {};
  activeAssignments.forEach(a => {
    const sId = a.sponsor_id;
    if (!groupedBySponsor[sId]) {
      const sp = sponsors.find(s => s.id === sId);
      groupedBySponsor[sId] = {
        name: a.sponsor_name || 'Unknown',
        logo: sp?.logo_url || '',
        items: [],
      };
    }
    groupedBySponsor[sId].items.push(a);
  });

  return (
    <div data-testid="rights-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('rights_management')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{t('manage_rights')}</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (
          <div className="flex items-center gap-2 mr-2">
            <Switch
              data-testid="show-inactive-toggle"
              checked={showInactive}
              onCheckedChange={v => { setShowInactive(v); localStorage.setItem('rights_show_inactive', v ? '1' : '0'); }}
            />
            <span className="text-xs text-gray-500">
              Show inactive / archived
              {!showInactive && hiddenCount > 0 && (
                <span data-testid="hidden-count" className="ms-1.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">{hiddenCount} hidden</span>
              )}
            </span>
          </div>
          )}
          {/* Assign Right Dialog */}
          {hasPerm('edit_rights') && (
          <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
            <DialogTrigger asChild>
              <Button data-testid="assign-right-btn" variant="outline" className="text-sm border-gray-200 h-9">
                <LinkIcon size={14} className="mr-1.5" /> {t('assign_right')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-md">
              <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('assign_right')}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-base text-gray-700">{t('sponsor')} *</Label>
                  <Select value={assignForm.sponsor_id} onValueChange={v => setAssignForm(p => ({...p, sponsor_id: v}))}>
                    <SelectTrigger data-testid="assign-sponsor-select" className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder={t('select_sponsor') || 'Select sponsor'} /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {activeSponsors.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-base text-gray-700">{t('right')} *</Label>
                  <Select value={assignForm.right_id} onValueChange={v => setAssignForm(p => ({...p, right_id: v}))}>
                    <SelectTrigger data-testid="assign-right-select" className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder={t('select_right')} /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {activeRights.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          <div className="flex items-center gap-2">
                            <RightIcon iconUrl={r.icon_url} size={16} className="text-gray-500" />
                            <span>{r.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-base text-gray-700">{t('allocation_type')}</Label>
                  <Select value={assignForm.allocation_type} onValueChange={v => setAssignForm(p => ({...p, allocation_type: v}))}>
                    <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="limited">{t('limited')}</SelectItem>
                      <SelectItem value="unlimited">{t('unlimited')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {assignForm.allocation_type === 'limited' && (
                  <div>
                    <Label className="text-base text-gray-700">{t('allocated_amount')} *</Label>
                    <Input data-testid="assign-amount-input" type="number" min="1" value={assignForm.allocated_amount} onChange={e => setAssignForm(p => ({...p, allocated_amount: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-base text-gray-700">Per Match Limit</Label>
                    <Input data-testid="assign-per-match-input" type="number" min="0" value={assignForm.per_match_limit} onChange={e => setAssignForm(p => ({...p, per_match_limit: e.target.value}))} placeholder="Unlimited" className="bg-gray-50 border-gray-300 h-11 text-base" />
                  </div>
                  <div>
                    <Label className="text-base text-gray-700">Per Round Limit</Label>
                    <Input data-testid="assign-per-round-input" type="number" min="0" value={assignForm.per_round_limit} onChange={e => setAssignForm(p => ({...p, per_round_limit: e.target.value}))} placeholder="Unlimited" className="bg-gray-50 border-gray-300 h-11 text-base" />
                  </div>
                </div>
                <p className="text-xs text-gray-400 -mt-2">Leave empty for unlimited per match/round</p>
                <Button data-testid="save-assign-btn" onClick={handleAssign} disabled={saving} className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-10">
                  {saving ? (t('saving') || 'Saving...') : (t('assign_right'))}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
          {/* Create Right Dialog */}
          {hasPerm('create_rights') && (
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-right-btn" className="bg-gray-900 hover:bg-gray-800 text-sm h-9"><Plus size={15} className="mr-1.5" /> {t('create_right')}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('create_right')}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><Label className="text-base text-gray-700">{t('name')} *</Label><Input data-testid="right-name-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
                <div><Label className="text-base text-gray-700">{t('description')}</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="bg-gray-50 border-gray-300 text-base" /></div>
                <div><Label className="text-base text-gray-700">{t('unit_type')}</Label>
                  <Select value={form.unit_type} onValueChange={v => setForm(p => ({...p, unit_type: v}))}>
                    <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">{unitTypes.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 bg-gray-50">
                  <div>
                    <Label className="text-base text-gray-700">{t('exclusive') || 'Exclusive'}</Label>
                    <p className="text-xs text-gray-400 mt-0.5">{t('exclusive_desc') || 'Only one sponsor can book this right per match'}</p>
                  </div>
                  <Switch data-testid="right-exclusive-toggle" checked={form.exclusive} onCheckedChange={v => setForm(p => ({...p, exclusive: v}))} />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 bg-gray-50">
                  <div>
                    <Label className="text-base text-gray-700">Match Required</Label>
                    <p className="text-xs text-gray-400 mt-0.5">When off, sponsors can request this right without selecting a match</p>
                  </div>
                  <Switch data-testid="right-match-required-toggle" checked={form.match_required} onCheckedChange={v => setForm(p => ({...p, match_required: v}))} />
                </div>
                <Button data-testid="save-right-btn" onClick={handleCreateRight} disabled={saving} className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-10">
                  {saving ? (t('saving') || 'Saving...') : t('create_right')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <Tabs defaultValue="assignments" className="w-full">
        <TabsList className="bg-gray-100 border-gray-200">
          <TabsTrigger value="assignments" className="text-base">{t('assignments')} ({activeAssignments.length})</TabsTrigger>
          {hasPerm('view_rights_master') && (
          <TabsTrigger value="rights" className="text-base">{t('rights_master')} ({filteredRights.length})</TabsTrigger>
          )}
        </TabsList>

        {/* === ASSIGNMENTS TAB === */}
        <TabsContent value="assignments" className="mt-4 space-y-8">
          {loading ? <p className="text-gray-400 text-center py-8">{t('loading')}</p> :
           Object.keys(groupedBySponsor).length === 0 ? <p className="text-gray-400 text-center py-8">{t('no_assignments')}</p> :
           Object.entries(groupedBySponsor).map(([sponsorId, group]) => (
            <div key={sponsorId} data-testid={`sponsor-group-${sponsorId}`}>
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                {group.logo ? (
                  <img src={group.logo} alt={group.name} className="w-10 h-10 rounded-lg object-contain bg-gray-50 border border-gray-200 p-1" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-400">
                    {group.name.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{group.name}</h3>
                  <p className="text-xs text-gray-400">{group.items.length} right(s) assigned</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {group.items.map(a => (
                  <Card key={a.id} data-testid={`assignment-card-${a.id}`} className={`bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow group relative ${a.right_status !== 'active' || a.sponsor_status !== 'active' ? 'opacity-60 border-dashed' : ''} ${hasPerm('edit_rights') ? 'cursor-pointer' : ''}`} onClick={() => hasPerm('edit_rights') && handleEditAssignment(a)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {a.right_icon_url && (
                            <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <RightIcon iconUrl={a.right_icon_url} size={16} className="text-gray-600" />
                            </div>
                          )}
                          <h3 className="font-semibold text-gray-800 text-sm leading-tight">{a.right_name}</h3>
                        </div>
                        {hasPerm('edit_rights') && <Pencil size={12} className="text-gray-300 group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />}
                      </div>
                      <div className="flex gap-1.5 mb-2">
                        <Badge variant="outline" className="capitalize text-[10px] pointer-events-none select-none">{a.unit_type}</Badge>
                        {a.right_status !== 'active' && (
                          <Badge data-testid={`assignment-right-status-${a.id}`} className="capitalize text-[10px] bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300 pointer-events-none select-none">{a.right_status}</Badge>
                        )}
                        {a.sponsor_status !== 'active' && (
                          <Badge className="capitalize text-[10px] bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 pointer-events-none select-none">sponsor {a.sponsor_status}</Badge>
                        )}
                      </div>
                      {a.allocation_type === 'unlimited' ? (
                        <div className="space-y-1.5 mt-2">
                          <div className="text-xs text-emerald-600 font-medium">Unlimited</div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">{t('used')}</span>
                            <span className="font-semibold text-amber-600">{a.used_amount || 0}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5 mt-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Season Total</span>
                            <span className="font-semibold text-gray-800">{a.allocated_amount}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Used</span>
                            <span className="font-medium text-gray-600">{a.used_amount || 0}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Remaining</span>
                            <span className="font-semibold text-green-600">{a.remaining_amount}</span>
                          </div>
                          {a.pending_amount > 0 && (
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-400">In Pending Requests</span>
                              <span className="font-medium text-amber-600">-{a.pending_amount}</span>
                            </div>
                          )}
                          {/* Usage bar — always shows USED */}
                          {(() => {
                            const pct = Math.round(((a.used_amount || 0) / (a.allocated_amount || 1)) * 100);
                            return (
                              <div data-testid={`assignment-usage-bar-${a.id}`} className="w-full bg-gray-100 dark:bg-[var(--chip)] rounded-full h-1.5 mt-1 overflow-hidden">
                                <div className={`rounded-full h-1.5 transition-all ${usageBarColor(pct)}`} style={{ width: `${Math.min(100, pct)}%` }} />
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {/* Per match/round limits */}
                      {(a.per_match_limit || a.per_round_limit) && (
                        <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                          {a.per_match_limit && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{a.per_match_limit}/match</span>
                          )}
                          {a.per_round_limit && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-purple-50 text-purple-600 font-medium">{a.per_round_limit}/round</span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* === RIGHTS MASTER TAB === */}
        <TabsContent value="rights" className="mt-4 space-y-4">
          <div className="relative max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder={t('search_rights')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-gray-300 h-10 text-sm" />
          </div>
          <Card className="bg-white border-gray-200/80 shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="border-gray-100 hover:bg-transparent">
                  <TableHead className="text-xs text-gray-400 font-medium w-10"></TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium">{t('name')}</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium">{t('type')}</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium hidden md:table-cell">{t('description')}</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium">{t('status')}</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium w-10"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">{t('loading')}</TableCell></TableRow> :
                  filteredRights.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">{t('no_rights')}</TableCell></TableRow> :
                  filteredRights.map(r => (
                    <TableRow key={r.id} className={`border-gray-100 hover:bg-gray-50 group ${r.status !== 'active' ? 'opacity-50' : ''}`}>
                      <TableCell className="w-10">
                        {r.icon_url ? (
                          <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
                            <RightIcon iconUrl={r.icon_url} size={16} className="text-gray-600" />
                          </div>
                        ) : (
                          <div className="w-7 h-7 rounded-md bg-gray-50 flex items-center justify-center text-gray-300">
                            <Image size={14} />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold text-gray-800 text-sm">
                        {r.name}
                        {r.exclusive && <Badge className="ml-2 text-[10px] bg-amber-100 text-amber-700 font-medium pointer-events-none select-none">{t('exclusive') || 'Exclusive'}</Badge>}
                      </TableCell>
                      <TableCell><Badge variant="outline" className="capitalize text-xs">{r.unit_type}</Badge></TableCell>
                      <TableCell className="hidden md:table-cell text-gray-500 text-sm max-w-xs truncate">{r.description}</TableCell>
                      <TableCell><Badge className={`text-[11px] pointer-events-none select-none ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{r.status}</Badge></TableCell>
                      <TableCell>
                        {(hasPerm('edit_rights') || hasPerm('delete_rights')) && (
                        <div className="flex items-center gap-0.5">
                          {hasPerm('edit_rights') && (
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0 text-gray-400 hover:text-primary" onClick={() => handleEditRight(r)}>
                            <Pencil size={14} />
                          </Button>
                          )}
                          {hasPerm('delete_rights') && (
                          <Button variant="ghost" size="sm" data-testid={`delete-right-${r.id}`} className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0 text-gray-400 hover:text-red-500" onClick={() => setDeleteRightConfirm(r)}>
                            <Trash2 size={14} />
                          </Button>
                          )}
                        </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* === Edit Right Dialog === */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditForm(null); } else setEditOpen(true); }}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('edit')} {t('right')}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4 mt-4">
              {/* Icon section */}
              <div>
                <Label className="text-base text-gray-700">Icon / Badge</Label>
                <div className="flex items-center gap-3 mt-2">
                  {editForm.icon_url ? (
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200 relative group/icon">
                      <RightIcon iconUrl={editForm.icon_url} size={24} className="text-gray-700" />
                      <button
                        onClick={() => handleRemoveIcon(editForm.id)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/icon:opacity-100 transition-opacity"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-300">
                      <Image size={20} />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIconPickerOpen(true)} className="border-gray-300 text-xs h-8">
                      Choose Icon
                    </Button>
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" className="border-gray-300 text-xs h-8 pointer-events-none" tabIndex={-1}>
                        <Upload size={12} className="mr-1" /> Upload
                      </Button>
                      <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) handleIconUpload(editForm.id, e.target.files[0]); }} />
                    </label>
                  </div>
                </div>
              </div>
              <div><Label className="text-base text-gray-700">{t('name')} *</Label><Input value={editForm.name || ''} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
              <div><Label className="text-base text-gray-700">{t('description')}</Label><Textarea value={editForm.description || ''} onChange={e => setEditForm(p => ({...p, description: e.target.value}))} className="bg-gray-50 border-gray-300 text-base" /></div>
              <div><Label className="text-base text-gray-700">{t('unit_type')}</Label>
                <Select value={editForm.unit_type} onValueChange={v => setEditForm(p => ({...p, unit_type: v}))}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">{unitTypes.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 bg-gray-50">
                <div>
                  <Label className="text-base text-gray-700">{t('exclusive') || 'Exclusive'}</Label>
                  <p className="text-xs text-gray-400 mt-0.5">{t('exclusive_desc') || 'Only one sponsor can book this right per match'}</p>
                </div>
                <Switch data-testid="edit-right-exclusive-toggle" checked={editForm.exclusive || false} onCheckedChange={v => setEditForm(p => ({...p, exclusive: v}))} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 bg-gray-50">
                <div>
                  <Label className="text-base text-gray-700">Match Required</Label>
                  <p className="text-xs text-gray-400 mt-0.5">When off, sponsors can request this right without selecting a match</p>
                </div>
                <Switch data-testid="edit-right-match-required-toggle" checked={editForm.match_required !== false} onCheckedChange={v => setEditForm(p => ({...p, match_required: v}))} />
              </div>
              <div><Label className="text-base text-gray-700">{t('status')}</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm(p => ({...p, status: v}))}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="active">{t('active') || 'Active'}</SelectItem>
                    <SelectItem value="inactive">{t('inactive') || 'Inactive'}</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="gap-2 pt-2">
                <Button variant="outline" onClick={() => { setEditOpen(false); setEditForm(null); }} className="border-gray-300">{t('cancel')}</Button>
                <Button onClick={handleSaveRight} disabled={saving} className="bg-gray-900 hover:bg-gray-800 text-sm">
                  {saving ? (t('saving') || 'Saving...') : (t('save_changes'))}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === Edit Assignment Dialog === */}
      <Dialog open={editAssignOpen} onOpenChange={(o) => { if (!o) { setEditAssignOpen(false); setEditAssignForm(null); } else setEditAssignOpen(true); }}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('edit')} {t('assignment') || 'Assignment'}</DialogTitle></DialogHeader>
          {editAssignForm && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {editAssignForm.right_icon_url && (
                  <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center">
                    <RightIcon iconUrl={editAssignForm.right_icon_url} size={18} className="text-gray-600" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{editAssignForm.right_name}</p>
                  <p className="text-xs text-gray-500">{editAssignForm.sponsor_name}</p>
                </div>
              </div>
              <div>
                <Label className="text-base text-gray-700">{t('allocation_type')}</Label>
                <Select value={editAssignForm.allocation_type} onValueChange={v => setEditAssignForm(p => ({...p, allocation_type: v}))}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="limited">{t('limited')}</SelectItem>
                    <SelectItem value="unlimited">{t('unlimited')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editAssignForm.allocation_type === 'limited' && (
                <div>
                  <Label className="text-base text-gray-700">{t('allocated_amount')}</Label>
                  <Input type="number" min="1" value={editAssignForm.allocated_amount || ''} onChange={e => setEditAssignForm(p => ({...p, allocated_amount: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" />
                  {editAssignForm.used_amount > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{t('used')}: {editAssignForm.used_amount} — {t('minimum')}: {editAssignForm.used_amount}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-base text-gray-700">Per Match Limit</Label>
                  <Input data-testid="edit-per-match-input" type="number" min="0" value={editAssignForm.per_match_limit || ''} onChange={e => setEditAssignForm(p => ({...p, per_match_limit: e.target.value}))} placeholder="Unlimited" className="bg-gray-50 border-gray-300 h-11 text-base" />
                </div>
                <div>
                  <Label className="text-base text-gray-700">Per Round Limit</Label>
                  <Input data-testid="edit-per-round-input" type="number" min="0" value={editAssignForm.per_round_limit || ''} onChange={e => setEditAssignForm(p => ({...p, per_round_limit: e.target.value}))} placeholder="Unlimited" className="bg-gray-50 border-gray-300 h-11 text-base" />
                </div>
              </div>
              <p className="text-xs text-gray-400 -mt-2">Leave empty for unlimited per match/round</p>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 bg-gray-50">
                <div>
                  <Label className="text-base text-gray-700">Match Required</Label>
                  <p className="text-xs text-gray-400 mt-0.5">When off, this right can be requested without a match</p>
                </div>
                <Switch data-testid="edit-assign-match-required" checked={editAssignForm.match_required !== false} onCheckedChange={v => setEditAssignForm(p => ({...p, match_required: v}))} />
              </div>
              <DialogFooter className="flex gap-2 pt-2">
                {hasPerm('delete_rights') && (
                <>
                <Button variant="outline" onClick={() => setConfirmDeleteAssign(true)} disabled={saving} className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
                  <Trash2 size={14} className="mr-1.5" /> {t('delete')}
                </Button>
                <ConfirmDialog open={confirmDeleteAssign} onCancel={() => setConfirmDeleteAssign(false)} onConfirm={handleDeleteAssignment}
                  title="Delete Assignment" description="Are you sure you want to delete this assignment?" confirmText="Delete" destructive />
                </>
                )}
                <div className="flex-1" />
                <Button variant="outline" onClick={() => { setEditAssignOpen(false); setEditAssignForm(null); }} className="border-gray-300">{t('cancel')}</Button>
                <Button data-testid="save-edit-assign-btn" onClick={handleSaveAssignment} disabled={saving} className="bg-gray-900 hover:bg-gray-800 text-sm">
                  {saving ? (t('saving') || 'Saving...') : (t('save_changes'))}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === Icon Picker === */}
      <IconPicker
        open={iconPickerOpen}
        value={editForm?.icon_url || ''}
        onChange={(val) => {
          if (editForm) {
            setEditForm(p => ({ ...p, icon_url: val }));
          }
        }}
        onClose={() => setIconPickerOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteRightConfirm}
        onCancel={() => setDeleteRightConfirm(null)}
        onConfirm={() => handleDeleteRight(deleteRightConfirm)}
        title="Delete Right"
        description={`Permanently delete "${deleteRightConfirm?.name}"? This cannot be undone. Any linked assignments must be removed first.`}
        confirmText="Delete"
      />

      {/* === Deactivation impact warning === */}
      <Dialog open={!!deactivateImpact} onOpenChange={o => { if (!o) setDeactivateImpact(null); }}>
        <DialogContent data-testid="deactivate-impact-dialog" className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {editForm?.status === 'archived' ? 'Archive' : 'Deactivate'} "{deactivateImpact?.right_name}"?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Sponsors', value: deactivateImpact?.sponsors || 0 },
                { label: 'Assignments', value: deactivateImpact?.assignments || 0 },
                { label: 'Open requests', value: deactivateImpact?.open_requests || 0 },
              ].map(s => (
                <div key={s.label} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2 text-center">
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{s.value}</p>
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>
            {deactivateImpact?.open_request_numbers?.length > 0 && (
              <p data-testid="impact-request-numbers" className="text-xs text-gray-500 dark:text-gray-400">
                Still open: {deactivateImpact.open_request_numbers.map(n => `#${n}`).join(', ')}
                {deactivateImpact.open_requests > deactivateImpact.open_request_numbers.length && ' …'}
              </p>
            )}
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-200 space-y-1">
              <p>No one will be able to request this right any more — including admins.</p>
              <p>Existing assignments stay in place and the {deactivateImpact?.open_requests || 0} open request(s) can still be reviewed, approved and delivered normally.</p>
              <p>{deactivateImpact?.total_used || 0} unit(s) of usage history are kept and stay visible in reports, tagged as inactive.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="impact-cancel-btn" onClick={() => setDeactivateImpact(null)} className="text-sm border-gray-200 dark:border-gray-700">Cancel</Button>
            <Button data-testid="impact-confirm-btn" onClick={handleSaveRight} disabled={saving} className="text-sm bg-red-600 hover:bg-red-700 text-white">
              {saving ? 'Saving…' : (editForm?.status === 'archived' ? 'Archive anyway' : 'Deactivate anyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
